"use client";
import TransitionLink from "../transitionLink";
import MarketingSvg from "../svg/marketing";
import { AnimatePresence, m } from "framer-motion";
import { useAppContext } from "../appContext";
import { ServiceMenuItem } from "./serviceMenuItem";
import Button from "../button";
import CalendlyButton from "../calendlyButton";
import { usePathname } from "next/navigation";
import LanguageSwitch from "./languageSwitch";

interface HeaderProps {
  data?: any;
  translations?: { en: string; cs: string };
}

const menuVariants = {
  initial: {
    y: "-100%",
  },
  animate: {
    y: 0,
    transition: {
      duration: 0.3,
      staggerChildren: 0.05,
      delayChildren: 0.2,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    y: "-100%",
    transition: {
      duration: 0.3,
      staggerChildren: 0.05,
      staggerDirection: -1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const menuItemVariants = {
  initial: (index: number) => ({
    y: index * -64,
    opacity: 0, // Add opacity to enhance visibility of staggering effect
  }),
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: (index: number) => ({
    y: index * -32,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

function HeaderDesktop({ data, translations }: HeaderProps) {
  const { menu, toggleMenu, openLightbox, trackCalendlyClick } = useAppContext();
  const pathname = usePathname();

  return (
    <>
      <div
        className={`hidden 2xl:block relative z-[2] w-full shrink-0 bg-white transition-all shadow-lg shadow-neutral-800/5 ${
          menu ? "border-b border-black" : ""
        }`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center gap-6 py-4">
          <TransitionLink
            href="/"
            classes="logo-link"
            onPointerUp={() => toggleMenu(null)}
            title="Navigate to homepage"
          >
            <MarketingSvg />
          </TransitionLink>
          <nav className="flex flex-nowrap gap-4 min-[1720px]:gap-8 items-center">
            {data?.menu &&
              data.menu.map((item: any, index: number) => {
                if (item.type === "page") {
                  return (
                    <TransitionLink
                      key={`item-${index}`}
                      href={`/${item.page?.slug?.current}`}
                      classes={`md:py-2 mono whitespace-nowrap shrink-0 hover:opacity-70 transition-opacity ${
                        pathname.includes(item.page?.slug?.current)
                          ? "opacity-100"
                          : ""
                      }`}
                      onPointerUp={() => toggleMenu(null)}
                    >
                      {item.title}
                    </TransitionLink>
                  );
                } else if (item.type === "file") {
                  return (
                    <a
                      key={`item-${index}`}
                      href={item.file.asset.url}
                      target="_blank"
                      //download={item.file.asset.originalFilename}
                      className="md:py-2 mono whitespace-nowrap shrink-0 hover:opacity-70 transition-opacity"
                    >
                      {item.title}
                    </a>
                  );
                } else {
                  return (
                    <div key={`toggle-${index}`}>
                      <button
                        className="md:py-2 flex items-center gap-1.5 mono whitespace-nowrap shrink-0 group"
                        onClick={() => toggleMenu(item.title)}
                        aria-expanded={menu === item.title}
                        aria-label={`${item.title} submenu`}
                      >
                        <span className="group-hover:opacity-70 transition-opacity">
                          {item.title}
                        </span>
                        <span className="inline-flex items-center justify-center w-[14px] h-[14px] shrink-0 bg-neutral-200 text-black border border-neutral-300 rounded-sm group-hover:bg-neutral-500/20 group-hover:border-neutral-500/30 transition-colors" aria-hidden="true">
                          <svg viewBox="0 0 7 7" fill="currentColor" className="w-[7px] h-[7px]" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 0h1v7H3z M0 3h7v1H0z" />
                          </svg>
                        </span>
                      </button>
                    </div>
                  );
                }
              })}
            <div className="flex shrink-0 gap-2">
              <LanguageSwitch translations={translations} />

              {data?.cta && (
                <Button
                  className="btn whitespace-nowrap shrink-0"
                  onClick={() => openLightbox("cta")}
                >
                  {data.cta}
                </Button>
              )}
              {data?.bookCallCta?.link && (
                <CalendlyButton
                  url={data.bookCallCta.link}
                  variant="red"
                  className="whitespace-nowrap shrink-0"
                  onOpen={trackCalendlyClick}
                >
                  {data.bookCallCta.title}
                </CalendlyButton>
              )}
            </div>
          </nav>
        </div>
      </div>
      {data?.menu &&
        data.menu.map((item: any, index: number) => {
          if (item.menuItems) {
            return (
              <AnimatePresence key={`menu-${index}`}>
                {menu === item.title && (
                  <m.div
                    key={`menu-${index}`}
                    variants={menuVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="hidden 2xl:block w-screen h-auto min-h-0 overflow-y-auto overscroll-contain left-0 top-16 flex flex-col items-center"
                  >
                    {item.menuItems?.map((subItem: any, i: number) => (
                      <m.div
                        key={`subItem-${i}`}
                        variants={menuItemVariants}
                        custom={i} // Pass `i` for staggered animation
                        className={`w-full relative overflow-hidden`}
                        style={{ zIndex: 100 - i }}
                      >
                        <ServiceMenuItem subItem={subItem} isHeader />
                      </m.div>
                    ))}
                  </m.div>
                )}
              </AnimatePresence>
            );
          }
        })}
    </>
  );
}
export default HeaderDesktop;
